import {
  getAudits,
  getAuditById,
  getAuditsByModel,
  getAuditEvents,
  getAuditableTypes,
  getAuditTags,
  getAuditStatistics
} from "@/api/audit";

const defaultState = () => {
  return {
    audits: [],
    currentAudit: null,
    pagination: {
      current_page: 1,
      per_page: 15,
      total: 0,
      last_page: 1
    },
    filters: {
      event: '',
      auditable_type: '',
      user_id: '',
      tags: '',
      date_from: '',
      date_to: '',
      search: ''
    },
    filterOptions: {
      events: [],
      types: [],
      tags: []
    },
    statistics: null,
    loading: false
  }
}

export default {
  namespaced: true,
  state: defaultState(),
  
  mutations: {
    SET_AUDITS: (state, audits) => {
      state.audits = audits
    },
    SET_CURRENT_AUDIT: (state, audit) => {
      state.currentAudit = audit
    },
    SET_PAGINATION: (state, pagination) => {
      state.pagination = pagination
    },
    SET_FILTERS: (state, filters) => {
      state.filters = { ...state.filters, ...filters }
    },
    RESET_FILTERS: (state) => {
      state.filters = defaultState().filters
    },
    SET_FILTER_OPTIONS: (state, { type, data }) => {
      state.filterOptions[type] = data
    },
    SET_STATISTICS: (state, statistics) => {
      state.statistics = statistics
    },
    SET_LOADING: (state, loading) => {
      state.loading = loading
    },
    RESET_STATE: (state) => {
      Object.assign(state, defaultState())
    }
  },
  
  actions: {
    async fetchAudits({ commit, state }) {
      commit('SET_LOADING', true)
      try {
        const params = {
          ...state.filters,
          page: state.pagination.current_page,
          per_page: state.pagination.per_page
        }
        
        const { data } = await getAudits(params)
        
        if (data.code === 200) {
          commit('SET_AUDITS', data.data.data)
          commit('SET_PAGINATION', {
            current_page: data.data.current_page,
            per_page: data.data.per_page,
            total: data.data.total,
            last_page: data.data.last_page
          })
          return { success: true }
        }
      } catch (error) {
        console.error('Error fetching audits:', error)
        return { success: false, error }
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchAuditById({ commit }, id) {
      try {
        const { data } = await getAuditById(id)
        if (data.code === 200) {
          commit('SET_CURRENT_AUDIT', data.data)
          return { success: true, data: data.data }
        }
      } catch (error) {
        console.error('Error fetching audit:', error)
        return { success: false, error }
      }
    },
    
    async fetchAuditsByModel({ commit }, { auditable_type, auditable_id }) {
      try {
        const { data } = await getAuditsByModel(auditable_type, auditable_id)
        if (data.code === 200) {
          return { success: true, data: data.data }
        }
      } catch (error) {
        console.error('Error fetching audits by model:', error)
        return { success: false, error }
      }
    },
    
    async fetchFilterOptions({ commit }) {
      try {
        const [eventsRes, typesRes, tagsRes] = await Promise.all([
          getAuditEvents(),
          getAuditableTypes(),
          getAuditTags()
        ])
        
        if (eventsRes.data.code === 200) {
          commit('SET_FILTER_OPTIONS', { type: 'events', data: eventsRes.data.data })
        }
        if (typesRes.data.code === 200) {
          commit('SET_FILTER_OPTIONS', { type: 'types', data: typesRes.data.data })
        }
        if (tagsRes.data.code === 200) {
          commit('SET_FILTER_OPTIONS', { type: 'tags', data: tagsRes.data.data })
        }
        
        return { success: true }
      } catch (error) {
        console.error('Error fetching filter options:', error)
        return { success: false, error }
      }
    },
    
    async fetchStatistics({ commit, state }) {
      try {
        const params = {
          date_from: state.filters.date_from,
          date_to: state.filters.date_to
        }
        
        const { data } = await getAuditStatistics(params)
        if (data.code === 200) {
          commit('SET_STATISTICS', data.data)
          return { success: true, data: data.data }
        }
      } catch (error) {
        console.error('Error fetching statistics:', error)
        return { success: false, error }
      }
    },
    
    setFilters({ commit }, filters) {
      commit('SET_FILTERS', filters)
    },
    
    resetFilters({ commit }) {
      commit('RESET_FILTERS')
    },
    
    setPage({ commit }, page) {
      commit('SET_PAGINATION', { ...this.state.audit.pagination, current_page: page })
    },
    
    setPerPage({ commit }, perPage) {
      commit('SET_PAGINATION', { ...this.state.audit.pagination, per_page: perPage, current_page: 1 })
    },
    
    resetState({ commit }) {
      commit('RESET_STATE')
    }
  },
  
  getters: {
    audits: state => state.audits,
    currentAudit: state => state.currentAudit,
    pagination: state => state.pagination,
    filters: state => state.filters,
    filterOptions: state => state.filterOptions,
    statistics: state => state.statistics,
    loading: state => state.loading
  }
}
